export type GraphThemeColors = {
  fg: (alpha: number) => string;
  inverse: (alpha: number) => string;
  background: string;
};

export type FlowCanvasColors = {
  bgColor: string;
  dotColor: string;
  wireColor: string;
};

export type DotGridThemeColors = {
  dotBase: string;
  dotGlow: (alpha: number) => string;
  background: string;
};

const DARK_FLOW_BG = "#2b2b2b";
const DARK_FLOW_DOT = "rgba(255, 255, 255, 0.22)";
const DARK_FLOW_WIRE = "#9a9a9a";
const DARK_GRAPH_BACKGROUND = "#0B0B0E";
const LIGHT_FLOW_BG = "#f3f4f6";
const LIGHT_FLOW_DOT = "rgba(0, 0, 0, 0.12)";
const LIGHT_FLOW_WIRE = "#9ca3af";
const LIGHT_GRAPH_BACKGROUND = "#f9fafb";

function readCssVar(name: string): string {
  if (typeof document === "undefined") return "";
  return getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
}

/**
 * Normalizes any CSS color string the browser accepts into an RGB tuple for
 * canvas rgba() composition.
 */
function cssColorToRgbTuple(color: string): [number, number, number] {
  if (typeof document === "undefined" || !color) return [255, 255, 255];

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) return [255, 255, 255];

  ctx.fillStyle = color;
  const normalized = ctx.fillStyle;

  if (normalized.startsWith("#")) {
    const hex = normalized.slice(1);
    const expanded =
      hex.length === 3
        ? hex
            .split("")
            .map((channel) => channel + channel)
            .join("")
        : hex;
    return [
      parseInt(expanded.slice(0, 2), 16),
      parseInt(expanded.slice(2, 4), 16),
      parseInt(expanded.slice(4, 6), 16),
    ];
  }

  const match = normalized.match(/[\d.]+/g);
  if (match && match.length >= 3) {
    return [Number(match[0]), Number(match[1]), Number(match[2])];
  }

  return [255, 255, 255];
}

function rgbaFromTuple(
  rgb: [number, number, number],
  alpha: number,
): string {
  return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})`;
}

/**
 * Theme-aware palette for the assistant graph visualization canvas. Dark values
 * match the original hardcoded rgba/hex palette; light values derive from CSS
 * semantic tokens when available.
 */
export function getGraphColors(
  resolvedTheme?: string | null,
): GraphThemeColors {
  const isDark = resolvedTheme !== "light";
  const background =
    readCssVar("--color-background") ||
    (isDark ? DARK_GRAPH_BACKGROUND : LIGHT_GRAPH_BACKGROUND);
  const fgRgb = cssColorToRgbTuple(
    readCssVar("--color-foreground") || (isDark ? "#ffffff" : "#030712"),
  );
  const inverseRgb = cssColorToRgbTuple(
    readCssVar("--color-background-secondary") ||
      (isDark ? "#000000" : "#ffffff"),
  );

  return {
    fg: (alpha) => rgbaFromTuple(fgRgb, alpha),
    inverse: (alpha) => rgbaFromTuple(inverseRgb, alpha),
    background,
  };
}

/** Theme-aware React Flow canvas background, dot grid, and wire colors. */
export function getFlowCanvasColors(
  resolvedTheme?: string | null,
): FlowCanvasColors {
  const isDark = resolvedTheme !== "light";

  if (isDark) {
    return {
      bgColor:
        readCssVar("--color-background-neutral-hovered") || DARK_FLOW_BG,
      dotColor: DARK_FLOW_DOT,
      wireColor: DARK_FLOW_WIRE,
    };
  }

  return {
    bgColor: readCssVar("--color-background-neutral") || LIGHT_FLOW_BG,
    dotColor: LIGHT_FLOW_DOT,
    wireColor: LIGHT_FLOW_WIRE,
  };
}

/** Theme-aware palette for the login dot-grid canvas background. */
export function getDotGridColors(
  resolvedTheme?: string | null,
): DotGridThemeColors {
  const isDark = resolvedTheme !== "light";
  const background =
    readCssVar("--color-background") ||
    (isDark ? DARK_GRAPH_BACKGROUND : LIGHT_GRAPH_BACKGROUND);
  const fgRgb = cssColorToRgbTuple(
    readCssVar("--color-foreground") || (isDark ? "#ffffff" : "#111827"),
  );
  const baseAlpha = isDark ? 0.05 : 0.06;

  return {
    dotBase: rgbaFromTuple(fgRgb, baseAlpha),
    dotGlow: (alpha) => rgbaFromTuple(fgRgb, alpha),
    background,
  };
}
